import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { defineStore } from 'pinia';

import type { HttpRequestConfig } from '@/apis/dash.http';

import * as FacebookAllAPI from '@/apis/facebook';
import { getHttpErrorStatus } from '@/apis/http-errors';

type Campaign = NonNullable<
  Awaited<ReturnType<typeof FacebookAllAPI.getMarketingV2Campaigns>>['data']
>[number];
type MarketingV2AdsArgs = Parameters<typeof FacebookAllAPI.getMarketingV2Ads>[0];
type MarketingV2Ad = NonNullable<
  NonNullable<Awaited<ReturnType<typeof FacebookAllAPI.getMarketingV2Ads>>['data']>['data']
>[number];
type MarketingV2CampaignsArgs = Parameters<typeof FacebookAllAPI.getMarketingV2Campaigns>[0];
type Ids = (number | string)[] | undefined;

export type MetaAdAccount = {
  accountId: string;
  accountName: string;
  currency: string;
  brandIds: number[];
  [key: string]: unknown;
};

function toCampaignMap(campaignList: Campaign[] | undefined) {
  return (campaignList ?? []).reduce<Record<string, Campaign>>((acc, campaign) => {
    acc[campaign.sourceCampaignId] = campaign;
    return acc;
  }, {});
}

// Kept outside store state so the promises aren't wrapped in a reactive proxy.
const inFlightMarketingV2CampaignRequests = new Map<string, Promise<unknown>>();

function toSortedKeyPart(ids: Ids) {
  return [...(ids ?? [])].map(String).toSorted((idA, idB) => idA.localeCompare(idB));
}

function getMarketingV2CampaignsRequestKey({
  brandIds,
  adAccountIds,
  sourceCampaignIds,
}: {
  brandIds: Ids;
  adAccountIds: Ids;
  sourceCampaignIds: Ids;
}) {
  return JSON.stringify({
    brandIds: toSortedKeyPart(brandIds),
    adAccountIds: toSortedKeyPart(adAccountIds),
    sourceCampaignIds: toSortedKeyPart(sourceCampaignIds),
  });
}

export const useFacebookStore = defineStore('facebook', {
  resetOnBrandChange: true,
  state: () => ({
    pending: {
      marketingV2Campaigns: false,
      marketingV2Ads: false,
      fbAdsAccounts: false,
      sendImagesToMetaAds: false,
      sendVideosToMetaAds: false,
      createMarketingV2Ads: false,
    },
    campaignList: [] as unknown[],
    fbAdsAccounts: [] as Record<string, unknown>[],
    hasApiAccess: false,
    adAccounts: {} as Record<string, MetaAdAccount[] | undefined>,
    adAccountsLoading: false,
    marketingV2Campaigns: {} as Record<string, Campaign>,
    marketingV2Ads: [] as MarketingV2Ad[],
    marketingV2AdsNextOffset: null as number | null,
    marketingV2AdsCount: 0,
    error: {
      adAccounts: false,
      campaigns: false,
    },
  }),
  getters: {
    adAccountsList(state) {
      const clonedAdAccounts = cloneDeep(state.adAccounts);
      const uniqAdAccounts = Object.values(
        Object.entries(clonedAdAccounts).reduce<Record<string, MetaAdAccount>>(
          (acc, [brandId, adAccounts]) => {
            const brandIdAsInt = parseInt(brandId, 10);
            for (let i = 0; i < (adAccounts?.length ?? 0); i += 1) {
              const adAccount = adAccounts![i];
              if (!acc[adAccount.accountId]) {
                acc[adAccount.accountId] = adAccount;
                acc[adAccount.accountId].brandIds = [brandIdAsInt];
              } else if (!acc[adAccount.accountId].brandIds.includes(brandIdAsInt)) {
                acc[adAccount.accountId].brandIds.push(brandIdAsInt);
              }
            }
            return acc;
          },
          {},
        ),
      );
      const sortedUniqAdAccounts = uniqAdAccounts.toSorted((adAccountA, adAccountB) =>
        adAccountA.accountName.localeCompare(adAccountB.accountName),
      );
      return sortedUniqAdAccounts;
    },
    metaAdsAccountToCurrency(): Record<string, string> {
      const mapping: Record<string, string> = {};
      this.adAccountsList.forEach((account) => {
        mapping[account.accountId] = account.currency;
      });
      return mapping;
    },
  },
  actions: {
    async getCampaignList({ brandId }: { brandId: number }) {
      const response = FacebookAllAPI.getCampaignList({ brandId });
      // @ts-expect-error -- missing await: reads `data` off the promise, so the list is always undefined.
      this.campaignList = response?.data;
    },
    async fetchFbAccountsFromApi({
      brandId,
      fetchApi,
      connectFacebookAdsToken,
    }: Parameters<typeof FacebookAllAPI.getAccountsFromApi>[0]) {
      this.pending.fbAdsAccounts = true;
      try {
        const response = await FacebookAllAPI.getAccountsFromApi({
          brandId,
          fetchApi,
          connectFacebookAdsToken,
        });
        const data = response?.data || [];
        this.fbAdsAccounts = data.map((item: Record<string, unknown>) => ({ ...item, brandId }));
      } finally {
        this.pending.fbAdsAccounts = false;
      }
    },
    async fetchFbAccountsFromApiV2({
      brandId,
      connectFacebookAdsToken,
    }: Parameters<typeof FacebookAllAPI.getAccountsFromApiV2>[0]) {
      this.pending.fbAdsAccounts = true;
      try {
        const response = await FacebookAllAPI.getAccountsFromApiV2({
          brandId,
          connectFacebookAdsToken,
        });
        const data = response?.data || [];
        this.fbAdsAccounts = data.accounts.map((item: Record<string, unknown>) => ({
          ...item,
          brandId,
        }));
        this.hasApiAccess = data.has_api_access;
      } finally {
        this.pending.fbAdsAccounts = false;
      }
    },
    async getAdAccounts({ brandIds }: { brandIds?: number[] }) {
      const brandIdsToRequest = (brandIds ?? []).filter((brandId) => !this.adAccounts[brandId]);
      if (brandIdsToRequest.length === 0) return this.adAccounts;

      this.adAccountsLoading = true;
      try {
        const { data } = await FacebookAllAPI.getAccountsMultiBrandsFromApi(
          { brandIds: brandIdsToRequest },
          { camelizeKeys: true },
        );
        this.adAccounts = merge(this.adAccounts, data) ?? {};
      } catch (error) {
        if ((getHttpErrorStatus(error) ?? 0) >= 400) {
          this.error.adAccounts = true;
        }
      } finally {
        this.adAccountsLoading = false;
      }
      return this.adAccounts;
    },
    clearAdAccounts() {
      this.adAccounts = {};
      this.error.adAccounts = false;
    },
    async getMarketingV2Campaigns(
      { brandIds, adAccountIds, sourceCampaignIds }: Partial<MarketingV2CampaignsArgs>,
      axiosConfig?: HttpRequestConfig,
    ) {
      const outstandingSourceCampaignIds = sourceCampaignIds?.filter(
        (sourceCampaignId) => !this.marketingV2Campaigns[sourceCampaignId],
      );

      // Backend requires >=1 brand and >=1 ad account; campaign ids alone were always a 400.
      if (brandIds && brandIds.length > 0 && adAccountIds && adAccountIds.length > 0) {
        const requestKey = getMarketingV2CampaignsRequestKey({
          brandIds,
          adAccountIds,
          sourceCampaignIds: outstandingSourceCampaignIds,
        });
        // Several independent form composables ask for the same campaigns on the same tick.
        // Callers passing an axiosConfig opt out: they own an abort signal, and sharing one
        // request would let either caller's abort cancel the other's.
        const inFlightRequest = axiosConfig
          ? undefined
          : inFlightMarketingV2CampaignRequests.get(requestKey);
        if (inFlightRequest) {
          // The owning call records failures on `error.campaigns`; swallowing keeps a shared
          // rejection from surfacing as an unhandled rejection in a watcher.
          await inFlightRequest.catch(() => {});
          return;
        }

        this.pending.marketingV2Campaigns = true;
        const request = FacebookAllAPI.getMarketingV2Campaigns(
          {
            brandIds,
            adAccountIds,
            sourceCampaignIds: outstandingSourceCampaignIds,
          },
          axiosConfig,
        );
        if (!axiosConfig) {
          inFlightMarketingV2CampaignRequests.set(requestKey, request);
        }
        try {
          const response = await request;
          const payload = response?.data;
          if (payload) {
            const newCampaignMap = toCampaignMap(payload);
            this.marketingV2Campaigns = {
              ...this.marketingV2Campaigns,
              ...newCampaignMap,
            };
          }
        } catch (error) {
          if ((getHttpErrorStatus(error) ?? 0) >= 400) {
            this.error.campaigns = true;
          }
        } finally {
          // Only clear what this call registered, so an aborting caller can't evict a
          // concurrent caller's in-flight request.
          if (!axiosConfig) {
            inFlightMarketingV2CampaignRequests.delete(requestKey);
          }
          this.pending.marketingV2Campaigns = false;
        }
      }
    },
    async getMarketingV2Ads(
      {
        brandId,
        sourceCampaignIds,
        sourceAdIds,
        sort,
        search,
        effectiveStatuses,
        limit,
      }: Omit<MarketingV2AdsArgs, 'creativeTypes' | 'offset'>,
      axiosConfig?: HttpRequestConfig,
    ) {
      try {
        this.pending.marketingV2Ads = true;
        const response = await FacebookAllAPI.getMarketingV2Ads(
          {
            brandId,
            sourceCampaignIds,
            sourceAdIds,
            sort,
            search,
            effectiveStatuses,
            limit,
            offset: this.marketingV2AdsNextOffset || undefined,
          },
          axiosConfig,
        );
        const payload = response?.data;
        if (payload) {
          this.marketingV2Ads = [...this.marketingV2Ads, ...(payload?.data || [])];
          this.marketingV2AdsCount = payload?.paging?.count || 0;

          if (payload?.paging?.next) {
            const url = new URL(payload.paging.next);
            const nextOffset = url.searchParams.get('offset');
            this.marketingV2AdsNextOffset = nextOffset ? parseInt(nextOffset, 10) : null;
          } else {
            this.marketingV2AdsNextOffset = null;
          }
        }
      } finally {
        this.pending.marketingV2Ads = false;
      }
    },
    async clearMarketingV2Ads() {
      this.marketingV2Ads = [];
      this.marketingV2AdsNextOffset = null;
      this.marketingV2AdsCount = 0;
    },
    async updateFbAccount({
      brandId,
      fbAccountId,
      isMonitored,
    }: Parameters<typeof FacebookAllAPI.updateAccount>[0]) {
      return FacebookAllAPI.updateAccount({ brandId, fbAccountId, isMonitored });
    },
    async deleteFbAccount({
      brandId,
      fbAccountId,
    }: Parameters<typeof FacebookAllAPI.deleteAccount>[0]) {
      return FacebookAllAPI.deleteAccount({ brandId, fbAccountId });
    },
    async sendImagesToMetaAds({
      brandId,
      adAccountIds,
      imageUrls,
    }: Parameters<typeof FacebookAllAPI.sendImagesToMetaAds>[0]) {
      this.pending.sendImagesToMetaAds = true;
      try {
        return await FacebookAllAPI.sendImagesToMetaAds({ brandId, adAccountIds, imageUrls });
      } finally {
        this.pending.sendImagesToMetaAds = false;
      }
    },
    async sendVideosToMetaAds({
      brandId,
      adAccountIds,
      videoUrls,
    }: Parameters<typeof FacebookAllAPI.sendVideosToMetaAds>[0]) {
      this.pending.sendVideosToMetaAds = true;
      try {
        return await FacebookAllAPI.sendVideosToMetaAds({ brandId, adAccountIds, videoUrls });
      } finally {
        this.pending.sendVideosToMetaAds = false;
      }
    },
    async createMarketingV2Ads({
      brandId,
      sourceAdAccountId,
      ads,
    }: Parameters<typeof FacebookAllAPI.createMarketingV2Ads>[0]) {
      this.pending.createMarketingV2Ads = true;
      try {
        return await FacebookAllAPI.createMarketingV2Ads({
          brandId,
          sourceAdAccountId,
          ads,
        });
      } finally {
        this.pending.createMarketingV2Ads = false;
      }
    },
  },
});
